/**
 * @file use-product-form-media.ts
 *
 * Media state for the product form: the gallery image URLs (with their uploaded
 * attachment ids) and the downloadable files for digital products. Gallery images
 * upload through {@link uploadImage}; download files through {@link uploadDownloadFile}
 * (both to the real `/v1` API, returning the `media_assets` id the save uses).
 * In-flight counts drive the placeholder loaders. Per-file validation (type/size/slot)
 * lives in the drop zone; this hook owns the resulting URLs and surfaces upload
 * failures via `uploadErrors`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { ShopProduct } from '../../api/shop-types';
import { adminMessages } from '../../i18n/messages';
import { uploadImage, uploadDownloadFile } from '../../api/media';
import { MAX_PRODUCT_MEDIA_IMAGES } from './product-form-constants';
import type { DownloadableFile } from './product-form-types';

/** What {@link useProductFormMedia} returns to the form. */
export interface UseProductFormMediaResult {
  imageUrls: string[];
  imageIdByUrl: Record<string, string>;
  uploadingCount: number;
  downloads: DownloadableFile[];
  downloadUploadingCount: number;
  uploadErrors: string[];
  setUploadErrors: (errors: string[]) => void;
  /** True once any image/download differs from the seed (for dirty tracking). */
  isDirty: boolean;
  onImagesSelected: (files: File[]) => void;
  onImagesChange: (urls: string[]) => void;
  /** Appends images chosen from the media library (already-uploaded attachments). */
  addLibraryImages: (items: { url: string; id: string }[]) => void;
  onDownloadsSelected: (files: File[]) => void;
  onDownloadsChange: (files: DownloadableFile[]) => void;
}

function seedImages(product?: ShopProduct): {
  urls: string[];
  idByUrl: Record<string, string>;
} {
  const urls: string[] = [];
  const idByUrl: Record<string, string> = {};
  for (const img of product?.images ?? []) {
    if (img.src) {
      urls.push(img.src);
      if (img.id) {
        idByUrl[img.src] = img.id;
      }
    }
  }
  return { urls, idByUrl };
}

/**
 * Manages gallery images and downloadable files for the product form.
 *
 * @param product - The seed product (edit/duplicate), or `undefined` to create.
 *
 * @returns Image/download state, in-flight counts, and change handlers.
 */
export function useProductFormMedia(
  product?: ShopProduct,
): UseProductFormMediaResult {
  const intl = useIntl();
  const initial = seedImages(product);
  const [imageUrls, setImageUrls] = useState<string[]>(initial.urls);
  const [imageIdByUrl, setImageIdByUrl] = useState<Record<string, string>>(
    initial.idByUrl,
  );
  const [uploadingCount, setUploadingCount] = useState(0);
  const [downloads, setDownloads] = useState<DownloadableFile[]>(
    product?.downloads ?? [],
  );
  const [downloadUploadingCount, setDownloadUploadingCount] = useState(0);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);
  const seededProductRef = useRef(product);

  // Re-seed only when the source product reference changes (e.g. it resolves
  // after a fetch); the initial value is already seeded by `useState`.
  useEffect(() => {
    if (seededProductRef.current === product) {
      return;
    }
    seededProductRef.current = product;
    const seeded = seedImages(product);
    setImageUrls(seeded.urls);
    setImageIdByUrl(seeded.idByUrl);
    setDownloads(product?.downloads ?? []);
    setUploadingCount(0);
    setDownloadUploadingCount(0);
    setUploadErrors([]);
    setDirty(false);
  }, [product]);

  const onImagesSelected = useCallback(
    (files: File[]) => {
      const remaining = MAX_PRODUCT_MEDIA_IMAGES - imageUrls.length;
      const accepted = files.slice(0, Math.max(0, remaining));
      if (accepted.length === 0) {
        return;
      }
      setDirty(true);
      setUploadingCount((c) => c + accepted.length);
      for (const file of accepted) {
        void uploadImage(file)
          .then(({ id, src }) => {
            setImageUrls((prev) =>
              prev.length < MAX_PRODUCT_MEDIA_IMAGES ? [...prev, src] : prev,
            );
            setImageIdByUrl((prev) => ({ ...prev, [src]: id }));
          })
          .catch(() => {
            setUploadErrors((prev) => [
              ...prev,
              intl.formatMessage(adminMessages.formUploadFailed),
            ]);
          })
          .finally(() => setUploadingCount((c) => Math.max(0, c - 1)));
      }
    },
    [imageUrls.length, intl],
  );

  const onImagesChange = useCallback((urls: string[]) => {
    setDirty(true);
    setImageUrls(urls);
    setImageIdByUrl((prev) => {
      const next: Record<string, string> = {};
      for (const url of urls) {
        if (prev[url] !== undefined) {
          next[url] = prev[url];
        }
      }
      return next;
    });
  }, []);

  const addLibraryImages = useCallback(
    (items: { url: string; id: string }[]) => {
      if (items.length === 0) {
        return;
      }
      setDirty(true);
      setImageUrls((prev) => {
        const next = [...prev];
        for (const { url } of items) {
          if (next.length >= MAX_PRODUCT_MEDIA_IMAGES) {
            break;
          }
          if (!next.includes(url)) {
            next.push(url);
          }
        }
        return next;
      });
      setImageIdByUrl((prev) => {
        const next = { ...prev };
        for (const { url, id } of items) {
          next[url] = id;
        }
        return next;
      });
    },
    [],
  );

  const onDownloadsSelected = useCallback(
    (files: File[]) => {
      if (files.length === 0) {
        return;
      }
      setDirty(true);
      setDownloadUploadingCount((c) => c + files.length);
      for (const file of files) {
        void uploadDownloadFile(file)
          .then(({ id, src }) => {
            setDownloads((prev) => [...prev, { id, name: file.name, file: src }]);
          })
          .catch(() => {
            setUploadErrors((prev) => [
              ...prev,
              intl.formatMessage(adminMessages.formUploadFailed),
            ]);
          })
          .finally(() => setDownloadUploadingCount((c) => Math.max(0, c - 1)));
      }
    },
    [intl],
  );

  const onDownloadsChange = useCallback((files: DownloadableFile[]) => {
    setDirty(true);
    setDownloads(files);
  }, []);

  return {
    imageUrls,
    imageIdByUrl,
    uploadingCount,
    downloads,
    downloadUploadingCount,
    uploadErrors,
    setUploadErrors,
    isDirty: dirty,
    onImagesSelected,
    onImagesChange,
    addLibraryImages,
    onDownloadsSelected,
    onDownloadsChange,
  };
}
